import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { organizationService } from '../services/organizationService';
import { authService } from '../services/authService';

const SECTIONS = [
  { id: 'general', label: 'General settings', blurb: 'Organization and account basics' },
  { id: 'preferences', label: 'User preferences', blurb: 'Defaults for notes and AI drafts' },
  { id: 'security', label: 'Security settings', blurb: 'MFA and session safety' },
  { id: 'notifications', label: 'Notification settings', blurb: 'Email and in-app alerts' },
  { id: 'health', label: 'System health', blurb: 'SMTP, Claude, S3, Stripe status' },
  { id: 'audit', label: 'Audit log', blurb: 'Who signed what, when' },
  { id: 'team', label: 'Team & retention', blurb: 'Invites and record retention' },
];

const defaultPrefs = () => {
  try {
    return JSON.parse(localStorage.getItem('peleescribe_prefs') || '{}');
  } catch {
    return {};
  }
};

const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get('section') || 'general';
  const setSection = (id) => setSearchParams({ section: id });

  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Clinician');
  const [saving, setSaving] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(
    () => JSON.parse(localStorage.getItem('user') || '{}').mfaEnabled || false,
  );
  const [retention, setRetention] = useState({
    clinicalRecordYears: 7,
    aiProcessingDays: 30,
    mediaTempDays: 7,
    notes: '',
  });
  const [prefs, setPrefs] = useState(() => ({
    defaultNoteType: 'SOAP',
    aiAutoDraft: true,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    ...defaultPrefs(),
  }));
  const [notif, setNotif] = useState(() => ({
    emailAppointments: true,
    emailMessages: true,
    emailBilling: true,
    emailTasks: true,
    inAppToasts: true,
    ...(defaultPrefs().notifications || {}),
  }));
  const [health, setHealth] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [orgRes, memberRes, inviteRes, profileRes] = await Promise.all([
        organizationService.getMine().catch(() => null),
        organizationService.listMembers().catch(() => ({ data: [] })),
        organizationService.listInvites().catch(() => ({ data: [] })),
        authService.getProfile().catch(() => null),
      ]);
      setOrg(orgRes?.data || null);
      if (orgRes?.data?.retentionPolicy) {
        setRetention({
          clinicalRecordYears: orgRes.data.retentionPolicy.clinicalRecordYears ?? 7,
          aiProcessingDays: orgRes.data.retentionPolicy.aiProcessingDays ?? 30,
          mediaTempDays: orgRes.data.retentionPolicy.mediaTempDays ?? 7,
          notes: orgRes.data.retentionPolicy.notes || '',
        });
      }
      setMembers(memberRes?.data || []);
      setInvites(inviteRes?.data || []);
      const user = profileRes?.user || authService.getCurrentUser();
      setProfile(user);
      if (user?.notificationPrefs) {
        setNotif((n) => ({ ...n, ...user.notificationPrefs }));
      }
      if (user?.appPreferences) {
        setPrefs((p) => ({ ...p, ...user.appPreferences }));
      }
      if (!orgRes?.data) {
        setError('No organization linked yet. Complete onboarding to enable team invites.');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (section === 'health') {
      authService
        .getSystemHealth()
        .then((r) => setHealth(r.data || r))
        .catch(() => setHealth(null));
    }
    if (section === 'audit') {
      import('../config/api')
        .then(({ API_CONFIG }) =>
          fetch(`${API_CONFIG.baseURL}/audit-logs?limit=50`, {
            headers: {
              ...API_CONFIG.headers,
              Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            },
          }).then((r) => r.json()),
        )
        .then((j) => setAuditLogs(j.data || j || []))
        .catch(() => setAuditLogs([]));
    }
  }, [section]);

  const savePrefs = async () => {
    const next = { ...prefs, notifications: notif };
    localStorage.setItem('peleescribe_prefs', JSON.stringify(next));
    try {
      await authService.updateAppPreferences(prefs);
      await authService.updateNotificationPrefs(notif);
      alert('Preferences saved to your account (and this device)');
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Saved locally; server sync failed');
    }
  };

  const createInvite = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      setSaving(true);
      const res = await organizationService.createInvite({ email, role });
      const path = res.data?.acceptPath || `/team-invite/${res.data?.token}`;
      const full = `${window.location.origin}${path}`;
      window.prompt('Share this team invite link:', full);
      setEmail('');
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to create invite');
    } finally {
      setSaving(false);
    }
  };

  const toggleMfa = async () => {
    try {
      if (mfaEnabled) {
        await authService.disableMfa();
        setMfaEnabled(false);
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        u.mfaEnabled = false;
        localStorage.setItem('user', JSON.stringify(u));
        alert('MFA disabled');
      } else {
        await authService.enableMfa();
        setMfaEnabled(true);
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        u.mfaEnabled = true;
        localStorage.setItem('user', JSON.stringify(u));
        alert('Email MFA enabled. You will receive a code at each login.');
      }
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Failed to update MFA');
    }
  };

  const saveRetention = async (e) => {
    e.preventDefault();
    try {
      await organizationService.updateRetention(retention);
      alert('Retention policy saved');
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to save retention');
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="max-w-5xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl font-poppins font-semibold text-primary">Settings</h1>
            <p className="text-gray-500 mt-1">
              Configure your application preferences and settings.
            </p>
          </div>

          {/* Tile grid — same labels client expected, now clickable */}
          <div className="grid sm:grid-cols-2 gap-3">
            {SECTIONS.filter((s) => s.id !== 'team').map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSection(s.id)}
                className={`text-left rounded-xl border p-5 transition ${
                  section === s.id
                    ? 'border-primary bg-primary/5 shadow'
                    : 'border-gray-200 bg-gray-50 hover:bg-white hover:shadow'
                }`}
              >
                <div className="font-medium text-gray-900">{s.label}</div>
                <div className="text-sm text-gray-500 mt-1">{s.blurb}</div>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setSection('team')}
            className={`text-sm underline ${section === 'team' ? 'text-primary' : 'text-gray-500'}`}
          >
            Team invites &amp; retention →
          </button>

          {loading && <p>Loading…</p>}
          {error && <p className="text-amber-700 text-sm">{error}</p>}

          {!loading && section === 'general' && (
            <div className="bg-white rounded-xl shadow p-6 space-y-3">
              <h2 className="font-medium text-lg">General settings</h2>
              <div className="text-sm space-y-2">
                <div>
                  <span className="text-gray-500">Signed in as</span>
                  <div className="font-medium">{profile?.name || profile?.email || '—'}</div>
                  <div className="text-gray-500">{profile?.email}</div>
                </div>
                <div>
                  <span className="text-gray-500">Organization</span>
                  <div className="font-medium text-lg">{org?.name || 'Not linked'}</div>
                </div>
                <div>
                  <span className="text-gray-500">Role</span>
                  <div>{profile?.role || '—'}</div>
                </div>
              </div>
            </div>
          )}

          {!loading && section === 'preferences' && (
            <div className="bg-white rounded-xl shadow p-6 space-y-4">
              <h2 className="font-medium text-lg">User preferences</h2>
              <label className="flex flex-col gap-1 text-sm max-w-xs">
                Default progress note type
                <select
                  className="border rounded px-3 py-2"
                  value={prefs.defaultNoteType}
                  onChange={(e) => setPrefs({ ...prefs, defaultNoteType: e.target.value })}
                >
                  <option value="SOAP">SOAP</option>
                  <option value="DAP">DAP</option>
                  <option value="BIRP">BIRP</option>
                  <option value="progress">Progress narrative</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!prefs.aiAutoDraft}
                  onChange={(e) => setPrefs({ ...prefs, aiAutoDraft: e.target.checked })}
                />
                Offer Claude AI draft when creating progress notes
              </label>
              <label className="flex flex-col gap-1 text-sm max-w-xs">
                Timezone display
                <input
                  className="border rounded px-3 py-2"
                  value={prefs.timezone}
                  onChange={(e) => setPrefs({ ...prefs, timezone: e.target.value })}
                />
              </label>
              <button type="button" onClick={savePrefs} className="bg-primary text-white px-4 py-2 rounded-lg">
                Save preferences
              </button>
            </div>
          )}

          {!loading && section === 'security' && (
            <div className="bg-white rounded-xl shadow p-6 space-y-3">
              <h2 className="font-medium text-lg">Security settings</h2>
              <p className="text-sm text-gray-500">
                Free email OTP after password (uses your existing mailer).
              </p>
              <button className="bg-primary text-white px-4 py-2 rounded-lg" onClick={toggleMfa}>
                {mfaEnabled ? 'Disable email MFA' : 'Enable email MFA'}
              </button>
              <div className="text-xs text-gray-500">Status: {mfaEnabled ? 'On' : 'Off'}</div>
            </div>
          )}

          {!loading && section === 'notifications' && (
            <div className="bg-white rounded-xl shadow p-6 space-y-3">
              <h2 className="font-medium text-lg">Notification settings</h2>
              <p className="text-sm text-gray-500 mb-2">
                Stored on your account (not just this device). In-app inbox still stores messages;
                email respects these toggles.
              </p>
              {[
                ['emailAppointments', 'Email: appointment reminders & booking requests'],
                ['emailMessages', 'Email: new secure messages'],
                ['emailBilling', 'Email: billing / invoice events'],
                ['emailTasks', 'Email: task assignments'],
                ['inAppToasts', 'Show toast popups for live notifications'],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!notif[key]}
                    onChange={(e) => setNotif({ ...notif, [key]: e.target.checked })}
                  />
                  {label}
                </label>
              ))}
              <button type="button" onClick={savePrefs} className="bg-primary text-white px-4 py-2 rounded-lg mt-2">
                Save notification settings
              </button>
            </div>
          )}

          {!loading && section === 'health' && (
            <div className="bg-white rounded-xl shadow p-6 space-y-3">
              <h2 className="font-medium text-lg">System health</h2>
              <p className="text-sm text-gray-500">
                Quick checks so support tickets do not start with &quot;it doesn&apos;t work.&quot;
              </p>
              {!health ? (
                <p className="text-sm text-gray-500">Unable to load health status.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {Object.entries(health.checks || {}).map(([key, val]) => (
                    <li
                      key={key}
                      className={`flex justify-between gap-4 border rounded-lg px-3 py-2 ${
                        val.ok ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'
                      }`}
                    >
                      <span className="font-medium capitalize">{key}</span>
                      <span className="text-gray-600 text-right">
                        {val.ok ? 'OK' : 'Issue'} — {val.detail}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {!loading && section === 'audit' && (
            <div className="bg-white rounded-xl shadow p-6 space-y-3">
              <h2 className="font-medium text-lg">Audit log</h2>
              <p className="text-sm text-gray-500 mb-2">
                Recent clinical and billing actions (who signed / changed what).
              </p>
              {auditLogs.length === 0 ? (
                <p className="text-sm text-gray-500">No audit entries yet.</p>
              ) : (
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left">
                      <tr>
                        <th className="p-2">When</th>
                        <th className="p-2">Action</th>
                        <th className="p-2">Entity</th>
                        <th className="p-2">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((row) => (
                        <tr key={row._id} className="border-t">
                          <td className="p-2 whitespace-nowrap">
                            {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                          </td>
                          <td className="p-2">{row.action || row.event || '—'}</td>
                          <td className="p-2">
                            {row.entityType || row.resourceType || '—'}{' '}
                            {row.entityId ? String(row.entityId).slice(-6) : ''}
                          </td>
                          <td className="p-2 text-gray-500 truncate max-w-xs">
                            {row.details
                              ? typeof row.details === 'string'
                                ? row.details
                                : JSON.stringify(row.details).slice(0, 120)
                              : row.message || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {!loading && section === 'team' && (
            <div className="space-y-6">
              {org && (
                <form onSubmit={saveRetention} className="bg-white rounded-xl shadow p-6 space-y-3">
                  <h2 className="font-medium">Record retention layers</h2>
                  <div className="grid md:grid-cols-3 gap-3">
                    <label className="text-sm flex flex-col gap-1">
                      Clinical records (years)
                      <input
                        type="number"
                        min="1"
                        className="border rounded px-3 py-2"
                        value={retention.clinicalRecordYears}
                        onChange={(e) =>
                          setRetention({ ...retention, clinicalRecordYears: Number(e.target.value) })
                        }
                      />
                    </label>
                    <label className="text-sm flex flex-col gap-1">
                      AI processing (days)
                      <input
                        type="number"
                        min="1"
                        className="border rounded px-3 py-2"
                        value={retention.aiProcessingDays}
                        onChange={(e) =>
                          setRetention({ ...retention, aiProcessingDays: Number(e.target.value) })
                        }
                      />
                    </label>
                    <label className="text-sm flex flex-col gap-1">
                      Media temp (days)
                      <input
                        type="number"
                        min="1"
                        className="border rounded px-3 py-2"
                        value={retention.mediaTempDays}
                        onChange={(e) =>
                          setRetention({ ...retention, mediaTempDays: Number(e.target.value) })
                        }
                      />
                    </label>
                  </div>
                  <button type="submit" className="bg-primary text-white px-4 py-2 rounded-lg">
                    Save retention policy
                  </button>
                </form>
              )}

              {org && (
                <div className="bg-white rounded-xl shadow p-6">
                  <h2 className="font-medium mb-4">Invite team member</h2>
                  <form onSubmit={createInvite} className="flex flex-wrap gap-3 items-end">
                    <label className="flex flex-col gap-1 text-sm flex-1 min-w-[200px]">
                      Email
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="border rounded px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      Role
                      <select
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="border rounded px-3 py-2"
                      >
                        <option value="Clinician">Clinician</option>
                        <option value="Supervisor">Supervisor</option>
                        <option value="Technician">Technician</option>
                        <option value="Reception">Reception</option>
                        <option value="Billing">Billing</option>
                      </select>
                    </label>
                    <button
                      type="submit"
                      disabled={saving}
                      className="bg-primary text-white px-4 py-2 rounded-lg"
                    >
                      {saving ? 'Sending…' : 'Create invite'}
                    </button>
                  </form>
                  <ul className="text-sm divide-y mt-4">
                    {invites.map((inv) => (
                      <li key={inv._id} className="py-2 flex justify-between gap-2">
                        <span>
                          {inv.email} · {inv.role} · {inv.status}
                        </span>
                        {inv.status === 'pending' && (
                          <button
                            className="text-red-600 text-xs"
                            onClick={async () => {
                              await organizationService.revokeInvite(inv._id);
                              await load();
                            }}
                          >
                            Revoke
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {org && (
                <div className="bg-white rounded-xl shadow p-6">
                  <h2 className="font-medium mb-3">Team members</h2>
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-2">Name</th>
                        <th className="p-2">Email</th>
                        <th className="p-2">Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m) => (
                        <tr key={m._id} className="border-t">
                          <td className="p-2">{m.name}</td>
                          <td className="p-2">{m.email}</td>
                          <td className="p-2">{m.role}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
